/**
 * PaymentMethodSelector
 * Inline row that opens a modal to choose a payment method.
 * Styled to match the exchange rate row in the form.
 */

import { useMemo } from 'react';
import type { SupportedPaymentMethod } from '@nowramp/sdk';
import { getPaymentIcon, GenericCoinIcon, ChevronDown } from './ui/icons';
import { useT, type TranslationKey } from '../i18n';

// Map Banxa payment-method IDs to icon keys used by getPaymentIcon
const ICON_KEY_MAP: Record<string, string> = {
	'debit-credit-card': 'card',
	'bank-transfer': 'bank',
	'ach-bank-transfer': 'bank',
	'payid-bank-transfer': 'bank',
	'sepa-bank-transfer': 'bank',
	sepa: 'bank',
	'apple-pay': 'applepay',
	'google-pay': 'googlepay',
	paypal: 'paypal',
	revolut: 'revolut',
};

// Localizable short names (non-brand)
const LOCALIZABLE_SHORT_NAMES: Record<string, TranslationKey> = {
	'debit-credit-card': 'payment.name.card',
	'bank-transfer': 'payment.name.bankTransfer',
	'ach-bank-transfer': 'payment.name.ach',
};

// Brand names that stay in English
const BRAND_NAME_MAP: Record<string, string> = {
	'apple-pay': 'Apple Pay',
	'google-pay': 'Google Pay',
	'payid-bank-transfer': 'PayID',
	paypal: 'PayPal',
	revolut: 'Revolut',
};

// Compact names for inline card display (where space is tight)
const SHORT_NAME_MAP: Record<string, string> = {
	'sepa-bank-transfer': 'SEPA',
	sepa: 'SEPA',
	'bank-transfer': 'Bank',
	'ach-bank-transfer': 'ACH',
};

export function getMethodDisplayName(
	id: string,
	fullName: string,
	t?: (key: TranslationKey) => string,
	short = false,
): string {
	if (short) {
		const compact = SHORT_NAME_MAP[id];
		if (compact) return compact;
	}
	const brand = BRAND_NAME_MAP[id];
	if (brand) return brand;
	const key = LOCALIZABLE_SHORT_NAMES[id];
	if (key && t) return t(key);
	// Fallback: English defaults for when t is not provided
	if (key) {
		const fallbacks: Record<string, string> = {
			'debit-credit-card': 'Card',
			'bank-transfer': 'Bank Transfer',
			'ach-bank-transfer': 'ACH',
		};
		return fallbacks[id] || fullName;
	}
	return fullName;
}

export function getMethodIcon(methodId: string, icon?: string, size = 24): React.ReactNode {
	const iconKey = ICON_KEY_MAP[methodId];
	if (iconKey) return getPaymentIcon(iconKey, size);
	if (icon) return <img src={icon} alt="" width={size} height={size} style={{ borderRadius: '50%' }} />;
	return <GenericCoinIcon size={size} />;
}

export interface PaymentMethodSelectorProps {
	methods: SupportedPaymentMethod[];
	selectedId: string;
	onOpenModal: () => void;
	fiatCurrency: string;
}

export function PaymentMethodSelector({
	methods,
	selectedId,
	onOpenModal,
	fiatCurrency,
}: PaymentMethodSelectorProps) {
	const t = useT();
	const filtered = useMemo(
		() => methods.filter((m) => m.supportedFiats.includes(fiatCurrency)),
		[methods, fiatCurrency]
	);

	const selected = filtered.find((m) => m.id === selectedId) || filtered[0];

	if (filtered.length === 0) {
		return (
			<div
				className="flex items-center justify-between px-[18px] pt-[12px] pb-[14px]"
				style={{
					background: 'var(--t-surface)',
					borderBottomLeftRadius: 5,
					borderBottomRightRadius: 5,
				}}
			>
				<span className="text-[12px]" style={{ color: 'var(--t-text-muted)' }}>
					{t('payment.unavailableFor', { currency: fiatCurrency })}
				</span>
			</div>
		);
	}

	return (
		<>
			<div
				role="button"
				tabIndex={0}
				onClick={onOpenModal}
				onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenModal(); }}
				className="flex items-center justify-between w-full px-[18px] transition-colors"
				style={{
					background: 'var(--t-surface)',
					borderBottomLeftRadius: 5,
					borderBottomRightRadius: 5,
					cursor: 'pointer',
					height: 42,
				}}
			>
				<span className="text-[12px]" style={{ color: 'var(--t-text-secondary)' }}>
					{t('payment.label')}
				</span>
				<span className="flex items-center gap-2">
					<span className="flex items-center justify-center" style={{ width: 20, height: 20 }}>
						{selected && getMethodIcon(selected.id, selected.icon, 20)}
					</span>
					<span className="text-[13px] font-medium" style={{ color: 'var(--t-text)' }}>
						{selected ? getMethodDisplayName(selected.id, selected.name, t) : t('payment.select')}
					</span>
					<ChevronDown />
				</span>
			</div>
		</>
	);
}
